import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import type { ChartOfAccount, CreateChartOfAccount, UpdateChartOfAccount } from '@dcc-erp/shared';

import { api, apiJson } from '../api';

const key = (companyId: string | undefined) => ['coa', companyId] as const;

export function useCoa(companyId: string | undefined, activeOnly = true) {
  return useQuery({
    queryKey: [...key(companyId), { activeOnly }],
    queryFn: () => api<ChartOfAccount[]>(`/companies/${companyId}/coa?activeOnly=${activeOnly}`),
    enabled: !!companyId,
  });
}

export function useCoaRow(companyId: string | undefined, id: string | undefined) {
  return useQuery({
    queryKey: ['coa', companyId, id],
    queryFn: () => api<ChartOfAccount>(`/companies/${companyId}/coa/${id}`),
    enabled: !!companyId && !!id,
  });
}

export function useCreateCoa(companyId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: CreateChartOfAccount) =>
      apiJson<CreateChartOfAccount, ChartOfAccount>('POST', `/companies/${companyId}/coa`, data),
    onSuccess: () => qc.invalidateQueries({ queryKey: key(companyId) }),
  });
}

export function useUpdateCoa(companyId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateChartOfAccount }) =>
      apiJson<UpdateChartOfAccount, ChartOfAccount>(
        'PATCH',
        `/companies/${companyId}/coa/${id}`,
        data,
      ),
    onSuccess: () => qc.invalidateQueries({ queryKey: key(companyId) }),
  });
}

export function useDeleteCoa(companyId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      api<ChartOfAccount>(`/companies/${companyId}/coa/${id}`, { method: 'DELETE' }),
    onSuccess: () => qc.invalidateQueries({ queryKey: key(companyId) }),
  });
}
